import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Star } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function EditReviewModal({ open, review, onClose, onSaved }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState("pending");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (review) {
      setRating(review.rating || 5);
      setComment(review.comment || "");
      setStatus(review.status || "pending");
    }
  }, [review]);

  if (!review) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/moderation/reviews/${review.review_id}/edit`, { rating, comment, status });
      toast.success("Avaliação atualizada");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-review-modal" className="max-w-lg bg-white rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Editar avaliação</DialogTitle>
          <DialogDescription className="text-brand-textMuted">
            Ajuste a nota, o comentário ou o status desta indicação.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5 mt-2">
          <div>
            <p className="text-xs text-brand-textMuted mb-2">Sobre <strong className="text-brand-text">{review.place_name}</strong> — por {review.user_name}</p>
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-2">Nota</label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={`star-${n}`} type="button" data-testid={`edit-review-star-${n}`} onClick={() => setRating(n)}>
                  <Star className={`w-8 h-8 ${n <= rating ? "fill-brand-yellow text-brand-yellow" : "text-brand-border"}`} />
                </button>
              ))}
              <span className="ml-2 font-bold text-brand-textMuted">{rating}/5</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-2">Comentário</label>
            <Textarea data-testid="edit-review-comment" required rows={4} value={comment} onChange={(e) => setComment(e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl" />
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-2">Status</label>
            <select data-testid="edit-review-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-brand-bg border border-brand-border rounded-2xl h-11 px-3 font-bold">
              <option value="pending">Pendente</option>
              <option value="approved">Aprovado</option>
              <option value="rejected">Rejeitado</option>
            </select>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full font-bold">Cancelar</Button>
            <Button type="submit" data-testid="edit-review-save-btn" disabled={saving} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
